import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageCircle, Ticket, Send, Loader2, Paperclip,
  CheckCircle2, Clock, AlertCircle, XCircle, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function UserSupport() {
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('chat');
  const [isLoading, setIsLoading] = useState(true);
  
  // Chat state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '' });
  const [ticketScreenshot, setTicketScreenshot] = useState(null);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadData();
      const interval = setInterval(loadData, 10000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const loadData = async () => {
    if (!user?.id) return;
    
    try {
      // Load conversation
      const convRes = await fetch(`${API_URL}/api/chat/conversation/${user.id}`);
      if (convRes.ok) {
        const convData = await convRes.json();
        setConversation(convData);
        
        // Load messages
        const msgRes = await fetch(`${API_URL}/api/chat/messages/${convData.id}`);
        if (msgRes.ok) {
          const msgData = await msgRes.json();
          setMessages(msgData.messages || []);
          // Mark as read
          await fetch(`${API_URL}/api/chat/mark-read/${convData.id}?reader_type=user`, { method: 'POST' });
        }
      }
      
      // Load tickets
      const ticketsRes = await fetch(`${API_URL}/api/tickets?user_id=${user.id}`);
      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setTickets(ticketsData.tickets || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;
    
    setIsSending(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/send?conversation_id=${conversation.id}&sender_id=${user.id}&sender_name=${encodeURIComponent(user.name)}&sender_type=user&message=${encodeURIComponent(newMessage)}`, {
        method: 'POST'
      });
      if (res.ok) {
        setNewMessage('');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const createTicket = async () => {
    if (!ticketForm.subject || !ticketForm.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsCreatingTicket(true);
    try {
      let screenshotUrl = null;
      if (ticketScreenshot) {
        const formData = new FormData();
        formData.append('file', ticketScreenshot);
        const uploadRes = await fetch(`${API_URL}/api/tickets/upload-screenshot`, {
          method: 'POST',
          body: formData
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          screenshotUrl = uploadData.url;
        }
      }
      
      const res = await fetch(`${API_URL}/api/tickets?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&subject=${encodeURIComponent(ticketForm.subject)}&description=${encodeURIComponent(ticketForm.description)}&user_email=${encodeURIComponent(user.email || '')}&screenshot_url=${encodeURIComponent(screenshotUrl || '')}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Ticket created successfully');
        setShowNewTicket(false);
        setTicketForm({ subject: '', description: '' });
        setTicketScreenshot(null);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket({ ...data.ticket, responses: data.responses });
      }
    } catch (error) {
      console.error('Failed to load ticket:', error);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/respond?responder_id=${user.id}&responder_name=${encodeURIComponent(user.name)}&responder_type=user&message=${encodeURIComponent(ticketReply)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        setTicketReply('');
        loadTicketDetails(selectedTicket.id);
      }
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-muted/500';
      default: return 'bg-muted/500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'resolved': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'closed': return <XCircle className="h-4 w-4 text-muted-foreground" />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-lg">
            <span className="text-4xl">🤖</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Support Center</h1>
            <p className="text-blue-100 mt-1">
              Chat with admin or raise support tickets
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Chat with Admin
          </TabsTrigger>
          <TabsTrigger value="tickets" className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            My Tickets
            {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-blue-500" />
                Chat with Admin
              </CardTitle>
              <CardDescription>
                Have a quick question? Chat directly with the admin team.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg h-[400px] flex flex-col">
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet</p>
                      <p className="text-sm">Start a conversation with admin</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 rounded-lg ${
                              msg.sender_type === 'user'
                                ? 'bg-blue-500 text-white'
                                : 'bg-muted text-foreground'
                            }`}
                          >
                            <p>{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.sender_type === 'user' ? 'text-blue-100' : 'text-muted-foreground'}`}>
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    />
                    <Button onClick={sendMessage} disabled={isSending || !newMessage.trim()}>
                      {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="h-5 w-5 text-purple-500" />
                    My Support Tickets
                  </CardTitle>
                  <CardDescription>
                    Track your support requests and their status
                  </CardDescription>
                </div>
                <Button onClick={() => setShowNewTicket(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Ticket
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tickets yet</p>
                  <p className="text-sm">Create a ticket to report an issue</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => loadTicketDetails(ticket.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(ticket.status)}
                          <div>
                            <p className="font-medium">{ticket.subject}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`${getStatusColor(ticket.status)} text-white capitalize`}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Subject"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
              />
            </div>
            <div>
              <Textarea
                placeholder="Describe your issue in detail..."
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                rows={5}
              />
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="screenshot-upload"
                onChange={(e) => setTicketScreenshot(e.target.files?.[0])}
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('screenshot-upload')?.click()}
                className="w-full"
              >
                <Paperclip className="h-4 w-4 mr-2" />
                {ticketScreenshot ? ticketScreenshot.name : 'Attach Screenshot (Optional)'}
              </Button>
            </div>
            <Button onClick={createTicket} disabled={isCreatingTicket} className="w-full">
              {isCreatingTicket ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getStatusIcon(selectedTicket?.status)}
              {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Status Badge */}
              <div className="flex items-center gap-2 mb-4">
                <Badge className={`${getStatusColor(selectedTicket.status)} text-white capitalize`}>
                  {selectedTicket.status.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created {new Date(selectedTicket.created_at).toLocaleString()}
                </span>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 mb-4">
                <div className="space-y-3">
                  {/* Original ticket */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Your Issue</p>
                    <p className="text-sm">{selectedTicket.description}</p>
                    {selectedTicket.screenshot_url && (
                      <img 
                        src={`${API_URL}${selectedTicket.screenshot_url}`} 
                        alt="Screenshot" 
                        className="mt-2 rounded border max-h-48 object-contain"
                      />
                    )}
                  </div>
                  
                  {/* Responses */}
                  {selectedTicket.responses?.map((resp) => (
                    <div
                      key={resp.id}
                      className={`p-3 rounded-lg ${
                        resp.responder_type === 'user' ? 'bg-blue-50 ml-4' : 'bg-green-50 mr-4'
                      }`}
                    >
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {resp.responder_type === 'admin' ? '👤 Admin' : 'You'}
                      </p>
                      <p className="text-sm">{resp.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(resp.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}

                  {/* Resolution notes */}
                  {selectedTicket.resolution_notes && (
                    <div className="p-3 bg-green-100 rounded-lg">
                      <p className="text-xs font-medium text-green-700 mb-1">Resolution Notes</p>
                      <p className="text-sm text-green-800">{selectedTicket.resolution_notes}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Reply input */}
              {['open', 'in_progress'].includes(selectedTicket.status) && (
                <div className="pt-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a reply..."
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendTicketReply()}
                    />
                    <Button onClick={sendTicketReply} disabled={!ticketReply.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
